import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { formatMinor, type VendorJob, type VendorJobStatus } from '@eprintsmart/shared';
import {
  currentVendorId,
  getArtworkLinks,
  subscribeToJobs,
  updateJob,
  type ArtworkDownload,
} from '../services/jobs';

/**
 * The vendor's working view.
 *
 * Deliberately shows what a press needs to run the job and nothing more: the
 * spec, the quantity, what we pay them, the delivery city and the date it has
 * to be there by. The customer's price, name and street address are not in
 * the record this reads from.
 */
export function JobBoard({
  title,
  statuses,
  emptyMessage,
}: {
  title: string;
  statuses: VendorJobStatus[];
  emptyMessage: string;
}) {
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<VendorJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void currentVendorId().then(setVendorId);
  }, []);

  useEffect(() => {
    if (!vendorId) return;
    return subscribeToJobs(vendorId, statuses, (next) => {
      setJobs(next);
      setLoading(false);
    });
    // statuses is a literal array per route, so identity is stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, statuses.join(',')]);

  if (!vendorId && !loading) {
    return <Alert severity="warning">This account is not linked to a vendor.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h1">{title}</Typography>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
      ) : jobs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{emptyMessage}</Typography>
        </Paper>
      ) : (
        jobs.map((job) => <JobCard key={job.id} job={job} onError={setError} />)
      )}
    </Stack>
  );
}

function JobCard({ job, onError }: { job: VendorJob; onError: (msg: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<ArtworkDownload[] | null>(null);
  const [declining, setDeclining] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const fetchArtwork = () =>
    run(async () => {
      setFiles(await getArtworkLinks(job.id));
    });

  const move = (to: VendorJobStatus, extra: Record<string, string> = {}) =>
    run(() =>
      updateJob({ orderId: job.orderId, orderItemId: job.orderItemId, to, ...extra }),
    );

  const dueInDays = job.promisedBy
    ? Math.ceil((job.promisedBy - Date.now()) / 86_400_000)
    : null;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ flexGrow: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontWeight: 700 }}>{job.productName}</Typography>
            <Chip size="small" label={`${job.qty} units`} variant="outlined" />
            <Chip size="small" label={job.status.replace(/_/g, ' ')} color="primary" variant="outlined" />
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {job.orderNumber} · to {job.shipToCity}, {job.shipToState} {job.shipToPincode}
          </Typography>

          <Typography variant="body2" sx={{ mt: 1 }}>
            {job.printSpec.trimWidthMm} × {job.printSpec.trimHeightMm} mm ·{' '}
            {job.printSpec.bleedMm} mm bleed · {job.printSpec.sides} side
            {job.printSpec.sides === 1 ? '' : 's'} · {job.printSpec.colorMode} ·{' '}
            {job.printSpec.minDpi} DPI min
          </Typography>

          {job.selectedOptions.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {job.selectedOptions.map((o) => `${o.groupName}: ${o.valueName}`).join(' · ')}
            </Typography>
          )}

          {job.printInstructions && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                Print instructions
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {job.printInstructions}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ textAlign: 'right', minWidth: 150 }}>
          <Typography variant="h2" sx={{ fontSize: '1.25rem', color: 'secondary.main' }}>
            {formatMinor(job.vendorCostMinor)}
          </Typography>
          <Typography variant="caption" color="text.secondary">Your payment</Typography>

          {dueInDays != null && (
            <Chip
              size="small"
              sx={{ mt: 1, display: 'flex' }}
              variant="outlined"
              color={dueInDays < 0 ? 'error' : dueInDays <= 1 ? 'warning' : 'default'}
              label={
                dueInDays < 0
                  ? `${Math.abs(dueInDays)}d overdue`
                  : dueInDays === 0
                    ? 'Due today'
                    : `${dueInDays}d left`
              }
            />
          )}
        </Box>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          disabled={busy}
          onClick={fetchArtwork}
        >
          {files ? 'Refresh links' : 'Get artwork'}
        </Button>

        {job.status === 'ASSIGNED' && (
          <>
            <Button size="small" variant="contained" color="secondary" disabled={busy} onClick={() => move('ACCEPTED')}>
              Accept job
            </Button>
            <Button size="small" color="inherit" disabled={busy} onClick={() => setDeclining(true)}>
              Decline
            </Button>
          </>
        )}

        {job.status === 'ACCEPTED' && (
          <Button size="small" variant="contained" color="secondary" disabled={busy} onClick={() => move('IN_PRODUCTION')}>
            Start production
          </Button>
        )}

        {job.status === 'IN_PRODUCTION' && (
          <Button size="small" variant="contained" color="secondary" disabled={busy} onClick={() => move('READY')}>
            Mark ready
          </Button>
        )}

        {job.status === 'READY' && (
          <Button size="small" variant="contained" color="secondary" disabled={busy} onClick={() => setDispatching(true)}>
            Dispatch
          </Button>
        )}

        {job.status === 'DISPATCHED' && job.trackingNumber && (
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {job.courier} · {job.trackingNumber}
          </Typography>
        )}
      </Stack>

      {files && (
        <Box sx={{ mt: 1.5 }}>
          {files.length === 0 ? (
            <Alert severity="info">No artwork has been uploaded for this job yet.</Alert>
          ) : (
            <>
              {files.map((f) => (
                <Stack key={f.id} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    href={f.url}
                    startIcon={<DownloadIcon />}
                    download
                  >
                    {f.filename}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {(f.bytes / 1024 / 1024).toFixed(1)} MB
                    {f.widthMm ? ` · ${Math.round(f.widthMm)} × ${Math.round(f.heightMm ?? 0)} mm` : ''}
                  </Typography>
                </Stack>
              ))}
              <Typography variant="caption" color="text.secondary">
                These links expire in an hour. Come back for fresh ones if you need them again.
              </Typography>
            </>
          )}
        </Box>
      )}

      <DeclineDialog
        open={declining}
        onClose={() => setDeclining(false)}
        onConfirm={(reason) => {
          setDeclining(false);
          void move('DECLINED', { note: reason });
        }}
      />

      <DispatchDialog
        open={dispatching}
        onClose={() => setDispatching(false)}
        onConfirm={(courier, trackingNumber) => {
          setDispatching(false);
          void move('DISPATCHED', { courier, trackingNumber });
        }}
      />
    </Paper>
  );
}

function DeclineDialog({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Decline this job</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          It goes back for reassignment straight away. Telling us why helps us send you better work.
        </Typography>
        <TextField
          fullWidth
          autoFocus
          multiline
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Press booked until Friday, cannot make the date."
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" onClick={() => onConfirm(reason.trim() || 'No reason given.')}>
          Decline job
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DispatchDialog({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (courier: string, trackingNumber: string) => void;
}) {
  const [courier, setCourier] = useState('');
  const [tracking, setTracking] = useState('');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dispatch this job</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          The customer is told it has shipped, so the tracking number needs to be right.
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Courier"
            fullWidth
            autoFocus
            value={courier}
            onChange={(e) => setCourier(e.target.value)}
            placeholder="e.g. Delhivery, Blue Dart, DTDC"
          />
          <TextField
            label="Tracking / AWB number"
            fullWidth
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!courier.trim() || !tracking.trim()}
          onClick={() => onConfirm(courier.trim(), tracking.trim())}
        >
          Confirm dispatch
        </Button>
      </DialogActions>
    </Dialog>
  );
}
